import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { trackLandingPageVisit, trackEvent } from '../services/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';

const LandingPageMarketing = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [stats, setStats] = useState({ stories_generated: 1247, users: 893, engines: 16, accuracy_pct: 87 });
  const source = location.pathname === '/watch' ? 'watch' : 'landing';

  useEffect(() => {
    const utmSource = searchParams.get('utm_source') || 'direct';
    const utmMedium = searchParams.get('utm_medium') || '';
    const utmCampaign = searchParams.get('utm_campaign') || '';
    localStorage.setItem('utm_source', utmSource);
    localStorage.setItem('utm_medium', utmMedium);
    localStorage.setItem('utm_campaign', utmCampaign);
    trackLandingPageVisit(utmSource);

    fetch(`${API_URL}/api/public-stats`)
      .then(r => r.json())
      .then(d => setStats(d))
      .catch(() => {});
  }, [searchParams]);

  const handleCTA = () => {
    trackEvent('landing_cta_click', { source });
    localStorage.removeItem('past_story_viewed');
    localStorage.removeItem('free_story_used');
    localStorage.removeItem('onboarding_completed');
    localStorage.removeItem('story_generated');
    localStorage.removeItem('story_generation_in_progress');
    navigate(`/onboarding-new?source=${source}`);
  };

  return (
    <div
      className="fixed inset-0 overflow-hidden flex flex-col"
      data-testid="landing-page"
      style={{
        height: '100dvh',
        background: 'radial-gradient(ellipse at center, #1a2332 0%, #0a0e17 100%)',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      {/* Cosmic nebula bg */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(circle at 30% 40%, rgba(0,255,255,0.06) 0%, transparent 50%), radial-gradient(circle at 70% 60%, rgba(138,43,226,0.04) 0%, transparent 50%)',
      }} />

      {/* Stars — fewer on mobile, computed once */}
      <div className="absolute inset-0 pointer-events-none">
        {Array.from({ length: 36 }).map((_, i) => (
          <div key={i} className="absolute rounded-full" style={{
            width: i % 4 === 0 ? 2 : 1,
            height: i % 4 === 0 ? 2 : 1,
            background: `rgba(255,255,255,${0.2 + (i % 5) * 0.08})`,
            left: `${(i * 37) % 100}%`,
            top: `${(i * 23) % 100}%`,
          }} />
        ))}
      </div>

      {/* Nav — compact */}
      <nav className="relative z-10 px-4 sm:px-6 flex items-center justify-between w-full max-w-5xl mx-auto"
           style={{ paddingTop: 'clamp(10px, 2vh, 18px)', paddingBottom: 'clamp(6px, 1.5vh, 10px)' }}>
        <div className="flex items-center gap-2">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <circle cx="6" cy="6" r="2" fill="#92FFF7" />
            <circle cx="12" cy="4" r="2" fill="#74B3FF" />
            <circle cx="18" cy="6" r="2" fill="#5F42C9" />
            <line x1="6" y1="6" x2="12" y2="4" stroke="#92FFF7" strokeWidth="0.5" opacity="0.5" />
            <line x1="12" y1="4" x2="18" y2="6" stroke="#74B3FF" strokeWidth="0.5" opacity="0.5" />
          </svg>
          <span style={{
            fontFamily: "'Cormorant Garamond', serif",
            color: '#F3E5AB',
            fontSize: 'clamp(14px, 3.8vw, 18px)',
            fontWeight: 400,
            letterSpacing: '0.25em',
            textTransform: 'uppercase',
          }}>Taraanaya</span>
        </div>
        <button
          onClick={handleCTA}
          data-testid="nav-cta-btn"
          className="rounded-full transition-all hover:scale-105"
          style={{
            background: 'linear-gradient(135deg, #d4af37, #b8962e)',
            color: '#0a0e17',
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 500,
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            padding: 'clamp(6px, 1.5vh, 8px) clamp(14px, 4vw, 20px)',
            fontSize: 'clamp(10px, 2.8vw, 12px)',
          }}
        >
          Try Free
        </button>
      </nav>

      {/* HERO — fills remaining space, perfectly centered */}
      <section
        className="relative z-10 flex-1 flex flex-col items-center justify-center text-center w-full max-w-3xl mx-auto"
        data-testid="hero-section"
        style={{ padding: 'clamp(8px, 2vh, 20px) clamp(16px, 5vw, 24px)' }}
      >
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
                    className="flex flex-col items-center w-full">
          {/* Constellation — scales with viewport */}
          <svg width="clamp(90,20vw,120)" height="40" viewBox="0 0 120 40" style={{ width: 'clamp(90px, 22vw, 120px)', marginBottom: 'clamp(6px, 1.2vh, 10px)' }}>
            {[0, 1, 2, 3, 4].map(i => {
              const x = 15 + i * 22.5;
              const y = 20 - 12 * Math.sin((i / 4) * Math.PI);
              const colors = ['#F3E5AB', '#E8C278', '#D4AF37', '#BC9A2F', '#9E7830'];
              return (
                <g key={i}>
                  <circle cx={x} cy={y} r="4" fill={colors[i]} opacity="0.3" />
                  <polygon points={`${x},${y-5} ${x+2},${y} ${x},${y+5} ${x-2},${y}`} fill={colors[i]} />
                  <circle cx={x} cy={y} r="1.5" fill="white" />
                  {i < 4 && (
                    <line x1={x} y1={y} x2={15 + (i + 1) * 22.5} y2={20 - 12 * Math.sin(((i + 1) / 4) * Math.PI)} stroke={colors[i]} strokeWidth="0.5" opacity="0.4" />
                  )}
                </g>
              );
            })}
          </svg>

          {/* Brand — Cormorant Garamond gold uppercase (matches Past/Future Story Reels) */}
          <h1 style={{
            fontFamily: "'Cormorant Garamond', serif",
            color: '#F3E5AB',
            textShadow: '0 0 32px rgba(243,229,171,0.28)',
            fontWeight: 300,
            fontSize: 'clamp(38px, 11vw, 72px)',
            lineHeight: 1.05,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            marginBottom: 'clamp(4px, 0.8vh, 8px)',
          }}>Taraanaya</h1>
          <p style={{
            color: 'rgba(243,229,171,0.7)',
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 'clamp(9px, 2.4vw, 12px)',
            textTransform: 'uppercase',
            letterSpacing: '0.32em',
            fontWeight: 500,
            marginBottom: 'clamp(14px, 3vh, 24px)',
          }}>
            Decision Intelligence · Vedic AI
          </p>

          {/* Hook — gold serif for visual unity */}
          <h2 style={{
            color: '#F3E5AB',
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 300,
            fontSize: 'clamp(18px, 5.2vw, 30px)',
            lineHeight: 1.3,
            letterSpacing: '0.04em',
            marginBottom: 'clamp(10px, 2vh, 16px)',
            maxWidth: '32ch',
          }}>
            16 AI Engines Decode Your Birth Chart Into<br />
            <span style={{ color: '#92FFF7', fontStyle: 'italic', letterSpacing: '0.02em' }}>The Story of Your Life</span>
          </h2>
          <p style={{
            color: 'rgba(255,255,255,0.55)',
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 'clamp(11px, 3vw, 14px)',
            lineHeight: 1.45,
            marginBottom: 'clamp(14px, 3vh, 24px)',
            maxWidth: '40ch',
          }}>
            Career, wealth, marriage, health — every chapter predicted with algorithmic precision rooted in ancient Vedic wisdom.
          </p>

          {/* CTA — gold with matching serif uppercase treatment */}
          <motion.button
            onClick={handleCTA}
            data-testid="hero-cta-btn"
            className="rounded-full shadow-lg"
            style={{
              background: 'linear-gradient(135deg, #F3E5AB 0%, #D4AF37 60%, #B8962E 100%)',
              color: '#0a0e17',
              fontFamily: "'Cormorant Garamond', serif",
              fontWeight: 500,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              boxShadow: '0 0 40px rgba(212,175,55,0.35), inset 0 1px 0 rgba(255,255,255,0.3)',
              padding: 'clamp(12px, 2.4vh, 16px) clamp(24px, 7.5vw, 40px)',
              fontSize: 'clamp(12px, 3.4vw, 15px)',
              whiteSpace: 'nowrap',
              border: '1px solid rgba(243,229,171,0.5)',
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
          >
            Begin Your Journey — Free
          </motion.button>
          <p style={{
            color: 'rgba(243,229,171,0.45)',
            fontSize: 'clamp(9px, 2.3vw, 11px)',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            fontWeight: 500,
            marginTop: 'clamp(6px, 1.2vh, 10px)',
          }}>No signup · Takes 2 minutes</p>

          {/* Compact stat chips — all 4 in a row on desktop, 2x2 on phone */}
          <div
            className="grid grid-cols-4 gap-1.5 sm:gap-2 w-full"
            style={{
              maxWidth: 'clamp(280px, 70vw, 560px)',
              marginTop: 'clamp(14px, 3vh, 24px)',
            }}
          >
            {[
              { value: stats.stories_generated.toLocaleString() + '+', label: 'Stories' },
              { value: stats.engines, label: 'Engines' },
              { value: stats.accuracy_pct + '%', label: 'Accuracy' },
              { value: stats.users.toLocaleString() + '+', label: 'Users' },
            ].map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 + i * 0.08 }}
                className="text-center rounded-lg"
                style={{
                  background: 'rgba(243,229,171,0.04)',
                  border: '1px solid rgba(243,229,171,0.14)',
                  padding: 'clamp(6px, 1.5vh, 10px) clamp(4px, 1.2vw, 10px)',
                }}
                data-testid={`stat-${i}`}
              >
                <div style={{
                  color: '#F3E5AB',
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: 'clamp(14px, 4vw, 20px)',
                  fontWeight: 400,
                  lineHeight: 1.1,
                }}>{s.value}</div>
                <div style={{
                  color: 'rgba(243,229,171,0.5)',
                  fontSize: 'clamp(8px, 2.2vw, 10px)',
                  marginTop: 2,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  fontWeight: 500,
                }}>{s.label}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Footer — compact, absolute so doesn't steal hero space */}
      <footer
        className="relative z-10 text-center"
        style={{
          padding: 'clamp(4px, 1vh, 8px) 16px',
          borderTop: '1px solid rgba(255,255,255,0.05)',
        }}
      >
        <p style={{ color: 'rgba(255,255,255,0.22)', fontSize: 'clamp(8px, 2vw, 10px)' }}>
          Taraanaya Vedtech · Noida, India · taraanaya@taraanaya.ai
        </p>
      </footer>

      {/* WhatsApp Floating Button — shrinks on small screens */}
      <a
        href="https://wa.me/919711538848?text=Hi%20Taraanaya%20team!%20I%20want%20to%20know%20more%20about%20premium%20features."
        target="_blank"
        rel="noopener noreferrer"
        data-testid="whatsapp-float-btn"
        className="fixed z-50 rounded-full flex items-center justify-center shadow-xl transition-transform hover:scale-110"
        style={{
          bottom: 'clamp(14px, 3vh, 22px)',
          right: 'clamp(14px, 3vw, 22px)',
          width: 'clamp(44px, 10vw, 54px)',
          height: 'clamp(44px, 10vw, 54px)',
          background: '#25D366',
        }}
        onClick={() => trackEvent('whatsapp_click', { page: 'landing' })}
      >
        <svg width="60%" height="60%" viewBox="0 0 24 24" fill="white">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>
    </div>
  );
};

export default LandingPageMarketing;
